import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  createProject,
  getProject,
  ProjectDetails,
  updateProject,
} from '../services/adminApi';

const emptyProject: Omit<ProjectDetails, 'id'> = {
  title: '',
  summary: '',
  description: '',
  location: '',
  status: 'Active',
  projectType: '',
  completionDate: '',
  featured: false,
};

const ProjectEditor = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Omit<ProjectDetails, 'id'>>(emptyProject);
  const [loading, setLoading] = useState<boolean>(Boolean(id));
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    const loadProject = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getProject(id);
        setProject({
          title: data.title,
          summary: data.summary,
          description: data.description,
          location: data.location,
          status: data.status,
          projectType: data.projectType,
          completionDate: data.completionDate ? data.completionDate.slice(0, 10) : '',
          featured: data.featured,
        });
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : t('pages.projectEditorLoadFailed', 'Failed to load project'));
      } finally {
        setLoading(false);
      }
    };

    void loadProject();
  }, [id, t]);

  const handleFieldChange = (field: keyof Omit<ProjectDetails, 'id'>, value: string | boolean) => {
    setProject((current) => ({ ...current, [field]: value } as Omit<ProjectDetails, 'id'>));
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSaving(true);

    try {
      const payload = {
        ...project,
        completionDate: project.completionDate || null,
      };

      if (id) {
        await updateProject(id, payload);
      } else {
        await createProject(payload);
      }

      navigate('/admin/projects');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('pages.projectEditorSaveFailed', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-grid">
      <div className="section-heading">
        <h2>{id ? t('pages.projectEditorTitleEdit') : t('pages.projectEditorTitleNew')}</h2>
        <p>{t('pages.projectEditorDescription')}</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {loading ? (
        <div>{t('common.loading')}</div>
      ) : (
        <form className="form-grid" onSubmit={handleSave}>
          <div className="form-group">
            <label>{t('pages.projectEditorFieldTitle')}</label>
            <input
              className="form-control"
              value={project.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>{t('pages.projectEditorFieldSummary')}</label>
            <textarea
              className="form-control"
              rows={3}
              value={project.summary}
              onChange={(e) => handleFieldChange('summary', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>{t('pages.projectEditorFieldDescription')}</label>
            <textarea
              className="form-control"
              rows={6}
              value={project.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>{t('pages.projectEditorFieldLocation')}</label>
            <input
              className="form-control"
              value={project.location}
              onChange={(e) => handleFieldChange('location', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>{t('pages.projectEditorFieldStatus')}</label>
            <input
              className="form-control"
              value={project.status}
              onChange={(e) => handleFieldChange('status', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>{t('pages.projectEditorFieldType')}</label>
            <input
              className="form-control"
              value={project.projectType}
              onChange={(e) => handleFieldChange('projectType', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>{t('pages.projectEditorFieldCompletionDate')}</label>
            <input
              type="date"
              className="form-control"
              value={project.completionDate ?? ''}
              onChange={(e) => handleFieldChange('completionDate', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={project.featured}
                onChange={(e) => handleFieldChange('featured', e.target.checked)}
              />{' '}
              {t('pages.projectEditorFieldFeatured')}
            </label>
          </div>

          <div className="form-actions" style={{ gridColumn: '1 / -1' }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? t('common.loading') : t('common.save')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin/projects')}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ProjectEditor;
